import { AgentType } from '@server/ai';
import { useDispatch, useSelector } from 'react-redux';
import { selectSearchText } from './agentsChatsReducer';
import useAgentsChats from './useAgentsChats';
import { Stack } from '@mui/material';
import AgentsHomeCardsExpanded from './AgentsHomeCardsExpanded';
import AgentSideMenu from './agentSideMenu/AgentSideMenu';
import AgentChatEntryPoint from './AgentChatEntryPoint';
import { CHAT_AREA_MAX_WIDTH, FakeAgentType } from './aiAgentsConsts';
import ToggleExpandButton from './ToggleExpandButton';
import ClosePanelButton from './ClosePanelButton';
import AgentsHomeCardsCollapsed from './AgentsHomeCardsCollapsed';
import useAgentsHome from './useAgentsHome';
import AgentTopBar from './AgentTopBar';
import AiAssistantPage from '../pages/ai-assistant/AiAssistantPage';
import { resetActiveFakeAgent, selectActiveFakeAgentType } from './agentsHomeReducer';
import AgentsNotAvailable from './AgentsNotAvailable';
import useIsAnyAgentEnabled from './useIsAnyAgentEnabled';
import { AgentSettingsProvider } from './AgentSettingsContext';
import { ArtifactPanel } from '../common/artifacts/ArtifactPanel';
import { TableArtifactPanel } from '../common/artifacts/TableArtifactPanel';
import { useArtifacts } from '../common/artifacts/useArtifacts';
import { useTableArtifactsOptional } from '../common/artifacts/useTableArtifacts';
import { useArtifactPanelWidth } from '../../hooks/useArtifactPanelWidth';

const AgentsHomeContent = ({
  closePanel,
  onNewAgentClick,
  isPageView,
}: {
  closePanel?: () => void;
  onNewAgentClick: (type: AgentType) => void;
  isPageView?: boolean;
}) => {
  const searchText = useSelector(selectSearchText);
  const { isExpanded } = useAgentsHome();
  const isFullWidth = isExpanded || isPageView;
  const { activeAgent } = useAgentsChats();
  const activeFakeAgentType = useSelector(selectActiveFakeAgentType);
  const dispatch = useDispatch();
  const isAnyAgentEnabled = useIsAnyAgentEnabled();
  const { isArtifactPanelOpen } = useArtifacts();
  const tableArtifactContext = useTableArtifactsOptional();
  const isTablePanelOpen = tableArtifactContext?.isTablePanelOpen ?? false;
  const { panelWidth } = useArtifactPanelWidth();
  const isSidePanelOpen = isArtifactPanelOpen || isTablePanelOpen;

  if (!isAnyAgentEnabled) {
    return <AgentsNotAvailable />;
  }

  const panelButtons = (
    <Stack
      direction="row"
      gap={1}
      sx={
        isSidePanelOpen && !isPageView && isExpanded
          ? {
              position: 'absolute',
              right: '12px',
              top: '12px',
            }
          : {}
      }
    >
      <ToggleExpandButton />
      <ClosePanelButton closePanel={closePanel} />
    </Stack>
  );

  const renderFakeAgent = () => {
    if (!activeFakeAgentType) return null;
    switch (activeFakeAgentType) {
      case FakeAgentType.SupportAgent:
        return (
          <AiAssistantPage
            goBackCb={() => dispatch(resetActiveFakeAgent())}
            isCollapsed={!isFullWidth}
            handleClosePanel={closePanel}
          />
        );

      default:
        return null;
    }
  };

  return (
    <AgentSettingsProvider>
      <Stack
        height="100%"
        position="relative"
        sx={{
          flex: 1,
          alignItems: 'center',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {!activeAgent && !activeFakeAgentType && (
          <>
            {!isFullWidth && (
              <AgentsHomeCardsCollapsed closePanel={closePanel} onNewAgentClick={onNewAgentClick} />
            )}
            {isFullWidth && (
              <AgentsHomeCardsExpanded
                closePanel={closePanel}
                onNewAgentClick={onNewAgentClick}
                isPageView={isPageView}
              />
            )}
          </>
        )}
        {activeAgent && (
          <Stack
            position="relative" // This is needed in order to locate the "panelButtons" when they are absolutely positioned
            direction={isFullWidth ? 'row' : 'column'}
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            {isFullWidth && <AgentSideMenu />}
            {!isFullWidth && <AgentTopBar closePanel={closePanel} />}

            <Stack
              flex={1}
              overflow="hidden"
              sx={{
                width: isSidePanelOpen ? `calc(100% - ${panelWidth}px)` : '100%',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <AgentChatEntryPoint
                noteAlignment={isFullWidth ? 'center' : 'left'}
                maxWidth={CHAT_AREA_MAX_WIDTH}
                searchTermToHighlight={isFullWidth ? searchText : undefined}
                panelButtons={!isPageView && isFullWidth ? panelButtons : null}
              />
            </Stack>

            {/* Artifact Panel as part of the flex layout */}
            {isArtifactPanelOpen && <ArtifactPanel />}

            {/* Table Artifact Panel */}
            {isTablePanelOpen && <TableArtifactPanel />}
          </Stack>
        )}
        {renderFakeAgent()}
      </Stack>
    </AgentSettingsProvider>
  );
};

export default AgentsHomeContent;
